import React, { useState } from 'react';
import { Chain } from '../../types/payment.types';
import { chainsIcons } from '../../variables/chainIcons';
import ToggleSwitch from './ToggleSwitch';
import { useI18n } from '../../i18n/I18n';

interface ChainTokenListProps {
    chains: Chain[];
    selectedChains: string[];
    selectedTokens: string[];
    onToggleChain: (chainId: string) => void;
    onToggleToken: (chainId: string, tokenSymbol: string) => void;
}

export default function ChainTokenList({
    chains,
    selectedChains,
    selectedTokens,
    onToggleChain,
    onToggleToken,
}: ChainTokenListProps) {
    const { t } = useI18n();
    const [expandedChains, setExpandedChains] = useState<string[]>([]);

    const toggleExpand = (chainId: string) => {
        setExpandedChains(prev =>
            prev.includes(chainId) ? prev.filter(id => id !== chainId) : [...prev, chainId]
        );
    };

    const mainnetChains = chains.filter(c => c.network === 'mainnet');

    return (
        <div>
            <h3 className="text-txt-heading font-bold text-[15px] mb-4 pl-1">{t('setup.networksAndTokens')}</h3>
            <div className="space-y-3">
                {mainnetChains.map((chain) => {
                    const chainId = chain.id.toString();
                    const isExpanded = expandedChains.includes(chainId);
                    const isChainSelected = selectedChains.includes(chainId);
                    const chainIconUrl = chainsIcons[`${chain.name}-${chain.network}`];
                    const selectedTokensCount = chain.tokens.filter(
                        t => selectedTokens.includes(`${chainId}:${t.symbol}`)
                    ).length;

                    return (
                        <div
                            key={chain.id}
                            className={`rounded-2xl overflow-hidden transition-all duration-300`}
                            style={{ background: isExpanded ? 'var(--card-bg-hover)' : 'var(--card-bg)', border: '1px solid var(--card-border)' }}
                        >
                            {/* Chain Header */}
                            <div className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-4 cursor-pointer flex-1" onClick={() => toggleExpand(chainId)}>
                                    {chainIconUrl ? (
                                        <img
                                            src={chainIconUrl}
                                            className="w-10 h-10 rounded-full object-cover"
                                            alt={chain.displayName}
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-txt-secondary font-medium text-lg shrink-0" style={{ background: 'var(--color-surface)' }}>
                                            {chain.name.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                            <span className="text-txt-secondary text-sm font-medium capitalize">{chain.displayName || chain.name}</span>
                                            <p className="text-[10px] text-txt-muted px-2 py-0.5 rounded-full" style={{ background: 'var(--badge-bg)' }}>
                                                {selectedTokensCount} / {chain.tokens.length} {t('setup.tokens')}
                                            </p>
                                            <span className={`material-symbols-outlined text-txt-muted text-[16px] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                                                expand_more
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <ToggleSwitch
                                    checked={isChainSelected}
                                    onChange={() => onToggleChain(chainId)}
                                />
                            </div>

                            {/* Tokens List */}
                            {isExpanded && (
                                <div className="p-4 space-y-3 animate-in slide-in-from-top-2 duration-200" style={{ borderTop: '1px solid var(--card-border)', background: 'var(--badge-bg)' }}>
                                    {chain.tokens.map(token => {
                                        const tokenKey = `${chainId}:${token.symbol}`;
                                        const isTokenSelected = selectedTokens.includes(tokenKey);

                                        return (
                                            <div key={token.symbol} className="flex items-center justify-between pr-1">
                                                <div className="flex items-center gap-3">
                                                    {token.iconUrl ? (
                                                        <img
                                                            src={token.iconUrl}
                                                            className="w-6 h-6 rounded-full object-cover"
                                                            alt={token.symbol}
                                                        />
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] text-txt-heading" style={{ background: 'var(--color-surface)' }}>
                                                            {token.symbol.charAt(0)}
                                                        </div>
                                                    )}
                                                    <span className="text-txt-secondary text-sm">{token.symbol}</span>
                                                </div>
                                                <ToggleSwitch
                                                    checked={isTokenSelected}
                                                    onChange={() => onToggleToken(chainId, token.symbol)}
                                                    size="sm"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
